import { supabase } from './supabase';

// ── PROFILE ──────────────────────────────────────────────────
export async function getProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) console.error('getProfile error:', error);
  return data;
}

export async function saveProfile(userId, profile) {
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...profile, updated_at: new Date() });
  if (error) console.error('saveProfile error:', error);
  return !error;
}


// ── ASSESSMENTS ───────────────────────────────────────────────
export async function getAssessments(userId) {
  const { data, error } = await supabase
    .from('assessments')
    .select('*')
    .eq('user_id', userId);
  if (error) console.error('getAssessments error:', error);
  const result = {};
  (data || []).forEach(row => { result[row.control_id] = row.answer; });
  return result;
}

export async function saveAnswer(userId, controlId, answer) {
  const { error } = await supabase
    .from('assessments')
    .upsert(
      { user_id: userId, control_id: controlId, answer, updated_at: new Date() },
      { onConflict: 'user_id,control_id' }
    );
  if (error) console.error('saveAnswer error:', error);
  return !error;
}

// ── TASKS ─────────────────────────────────────────────────────
export async function getTasks(userId) {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) console.error('getTasks error:', error);
  return data || [];
}

export async function createTask(userId, task) {
  const { data, error } = await supabase
    .from('tasks')
    .insert({ user_id: userId, ...task })
    .select()
    .single();
  if (error) console.error('createTask error:', error);
  return data;
}

export async function updateTask(taskId, updates) {
  const { error } = await supabase
    .from('tasks')
    .update({ ...updates, updated_at: new Date() })
    .eq('id', taskId);
  if (error) console.error('updateTask error:', error);
  return !error;
}

export async function deleteTask(taskId) {
  const { error } = await supabase
    .from('tasks')
    .delete()
    .eq('id', taskId);
  if (error) console.error('deleteTask error:', error);
  return !error;
}

// ── EVIDENCE ──────────────────────────────────────────────────
export async function getEvidence(userId) {
  const { data, error } = await supabase
    .from('evidence')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) console.error('getEvidence error:', error);
  return data || [];
}

export async function createEvidence(userId, ev) {
  const { data, error } = await supabase
    .from('evidence')
    .insert({ user_id: userId, ...ev })
    .select()
    .single();
  if (error) console.error('createEvidence error:', error);
  return data;
}

export async function deleteEvidence(evidenceId, fileUrl) {
  // Remove file from storage first if one exists
  if (fileUrl) {
    const { error: storageErr } = await supabase.storage
      .from('evidence-files')
      .remove([fileUrl]);
    if (storageErr) console.warn('deleteEvidence storage error:', storageErr);
  }
  const { error } = await supabase
    .from('evidence')
    .delete()
    .eq('id', evidenceId);
  if (error) console.error('deleteEvidence error:', error);
  return !error;
}

export async function updateEvidenceStatus(evidenceId, status) {
  const { error } = await supabase
    .from('evidence')
    .update({ status })
    .eq('id', evidenceId);
  if (error) console.error('updateEvidenceStatus error:', error);
  return !error;
}

export async function uploadEvidenceFile(userId, file) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path     = `${userId}/${Date.now()}-${safeName}`;
  const { error } = await supabase.storage
    .from('evidence-files')
    .upload(path, file, { upsert: false });
  if (error) {
    console.error('uploadEvidenceFile error:', error);
    return { path: null, error: error.message };
  }
  return { path, error: null, fileType: file.type, fileSize: file.size, fileName: file.name };
}

export async function getEvidenceDownloadUrl(fileUrl) {
  const { data, error } = await supabase.storage
    .from('evidence-files')
    .createSignedUrl(fileUrl, 3600); // 1-hour signed URL
  if (error) {
    console.error('getEvidenceDownloadUrl error:', error);
    return { signedUrl: null, error: error.message };
  }
  return { signedUrl: data.signedUrl, error: null };
}

// ── AUDITOR ACCESS ────────────────────────────────────────────
export async function getAuditorAccess(userId) {
  const { data, error } = await supabase
    .from('auditor_access')
    .select('*')
    .eq('owner_id', userId)
    .order('created_at', { ascending: false });
  if (error) console.error('getAuditorAccess error:', error);
  return data || [];
}

export async function inviteAuditor(userId, auditorName, auditorFirm, email, framework, expiryDays) {
  const days    = expiryDays || 90;
  const expiry  = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  const { data, error } = await supabase
    .from('auditor_access')
    .insert({
      owner_id:      userId,
      auditor_email: email || '',
      auditor_name:  auditorName || '',
      auditor_firm:  auditorFirm || '',
      is_active:     true,
      framework:     framework || 'cmmc_l2',
      expiry_days:   days,
      expires_at:    expiry,
    })
    .select()
    .single();
  if (error) console.error('inviteAuditor error:', error);
  return data;
}

export async function revokeAuditor(accessId) {
  const { error } = await supabase
    .from('auditor_access')
    .update({ is_active: false })
    .eq('id', accessId);
  if (error) console.error('revokeAuditor error:', error);
  return !error;
}

export async function deleteAuditor(accessId) {
  const { error } = await supabase
    .from('auditor_access')
    .delete()
    .eq('id', accessId);
  if (error) console.error('deleteAuditor error:', error);
  return !error;
}

export async function getAuditorByToken(token) {
  const { data, error } = await supabase
    .from('auditor_access')
    .select('*')
    .eq('access_token', token)
    .eq('is_active', true)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') console.error('getAuditorByToken error:', error);
  return data || null;
}

export async function updateAuditorLastAccessed(token) {
  const { error } = await supabase
    .from('auditor_access')
    .update({ last_accessed_at: new Date().toISOString() })
    .eq('access_token', token);
  if (error) console.error('updateAuditorLastAccessed error:', error);
}

// ── ACTIVITY LOG ─────────────────────────────────────────────
export async function logActivity(userId, companyId, action, resourceType = null, metadata = {}) {
  if (!userId || !companyId) return;
  const { error } = await supabase.from('activity_log').insert({
    user_id:       userId,
    company_id:    companyId,
    action,
    resource_type: resourceType,
    metadata,
    created_at:    new Date().toISOString(),
  });
  if (error) console.error('logActivity error:', error);
}

export async function getActivityLog(companyId, limit = 50) {
  const { data, error } = await supabase
    .from('activity_log')
    .select('*, profiles(email, company_name)')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) console.error('getActivityLog error:', error);
  return data || [];
}

// ── AFFIRMATIONS ──────────────────────────────────────────────
export async function getAffirmations(userId) {
  const { data, error } = await supabase
    .from('affirmations')
    .select('*')
    .eq('user_id', userId)
    .order('affirmed_at', { ascending: false });
  if (error) console.error('getAffirmations error:', error);
  return data || [];
}

export async function createAffirmation(userId, affirmation) {
  const { data, error } = await supabase
    .from('affirmations')
    .insert({ user_id: userId, ...affirmation })
    .select()
    .single();
  if (error) console.error('createAffirmation error:', error);
  return data;
}

export async function deleteAffirmation(affirmationId) {
  const { error } = await supabase
    .from('affirmations')
    .delete()
    .eq('id', affirmationId);
  if (error) console.error('deleteAffirmation error:', error);
  return !error;
}

// ── AGENCY ────────────────────────────────────────────────────
export async function getAgency(userId) {
  const { data, error } = await supabase
    .from('agencies')
    .select('*')
    .eq('owner_id', userId)
    .single();
  if (error && error.code !== 'PGRST116') console.error('getAgency error:', error);
  return data;
}

export async function saveAgency(userId, agencyData) {
  const { data: existing } = await supabase
    .from('agencies')
    .select('id')
    .eq('owner_id', userId)
    .single();

  let data, error;

  if (existing) {
    const result = await supabase
      .from('agencies')
      .update({ ...agencyData, updated_at: new Date() })
      .eq('owner_id', userId)
      .select()
      .single();
    data  = result.data;
    error = result.error;
  } else {
    const result = await supabase
      .from('agencies')
      .insert({ owner_id: userId, ...agencyData })
      .select()
      .single();
    data  = result.data;
    error = result.error;
  }

  if (error) {
    console.error('saveAgency error:', error);
    return null;
  }

  if (agencyData.white_label_enabled !== undefined) {
    await supabase
      .from('profiles')
      .update({
        white_label_enabled:  agencyData.white_label_enabled,
        white_label_name:     agencyData.white_label_name     || '',
        white_label_color:    agencyData.white_label_color    || '#2D7DD2',
        white_label_logo_url: agencyData.white_label_logo_url || '',
      })
      .eq('id', userId);
  }

  return data;
}

export async function getAgencyClients(agencyId) {
  const { data, error } = await supabase
    .from('agency_clients')
    .select('*')
    .eq('agency_id', agencyId)
    .order('created_at', { ascending: false });
  if (error) console.error('getAgencyClients error:', error);
  return data || [];
}

export async function addAgencyClient(agencyId, client) {
  const { data, error } = await supabase
    .from('agency_clients')
    .insert({ agency_id: agencyId, ...client })
    .select()
    .single();
  if (error) console.error('addAgencyClient error:', error);
  return data;
}

export async function deleteAgencyClient(clientId) {
  const { error } = await supabase
    .from('agency_clients')
    .delete()
    .eq('id', clientId);
  if (error) console.error('deleteAgencyClient error:', error);
  return !error;
}

export async function getClientAssessments(clientUserId) {
  const { data, error } = await supabase
    .from('assessments')
    .select('*')
    .eq('user_id', clientUserId);
  if (error) console.error('getClientAssessments error:', error);
  const result = {};
  (data || []).forEach(row => { result[row.control_id] = row.answer; });
  return result;
}

export async function getClientTasks(clientUserId) {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('user_id', clientUserId)
    .order('created_at', { ascending: false });
  if (error) console.error('getClientTasks error:', error);
  return data || [];
}

// ── ADMIN ─────────────────────────────────────────────────────
// To grant admin access, run in Supabase SQL Editor:
//   INSERT INTO admin_users (user_id, role) VALUES ('your-user-uuid-here', 'admin');
// Find user UUIDs in the Supabase Auth > Users dashboard.

export async function isUserAdmin(userId) {
  const { data, error } = await supabase
    .from('admin_users')
    .select('id')
    .eq('user_id', userId)
    .single();
  if (error && error.code !== 'PGRST116') console.error('isUserAdmin error:', error);
  return !!data;
}

// Returns all profiles (requires admin RLS). Email is stored in profiles.email via trigger.
export async function getAllUsers() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, company_name, plan, account_status, is_admin, last_active, created_at')
    .order('created_at', { ascending: false });
  if (error) console.error('getAllUsers error:', error);
  return data || [];
}

// Returns structured assessment stats for a single user
export async function getUserAssessmentData(userId) {
  const { data, error } = await supabase
    .from('assessments')
    .select('answer')
    .eq('user_id', userId);
  if (error) console.error('getUserAssessmentData error:', error);

  const rows        = data || [];
  const total       = rows.length;
  const implemented = rows.filter(r => r.answer === 'yes').length;
  const partial     = rows.filter(r => r.answer === 'partial').length;
  const notImpl     = rows.filter(r => r.answer === 'no').length;
  return { total, implemented, partial, notImplemented: notImpl };
}

export async function updateUserPlanByAdmin(userId, plan) {
  const { error } = await supabase
    .from('profiles')
    .update({ plan, updated_at: new Date() })
    .eq('id', userId);
  if (error) console.error('updateUserPlanByAdmin error:', error);
  return !error;
}

export async function updateUserAccountStatus(userId, status) {
  const { error } = await supabase
    .from('profiles')
    .update({ account_status: status, updated_at: new Date() })
    .eq('id', userId);
  if (error) console.error('updateUserAccountStatus error:', error);
  return !error;
}

// ── COMPANY / MULTI-USER ──────────────────────────────────────

export async function generateCompanyCode(companyName) {
  const chars  = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous O/0/1/I
  const prefix = (companyName || 'XXXXX')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, 'X')
    .padEnd(6, 'X')
    .slice(0, 6);

  for (let attempt = 0; attempt < 10; attempt++) {
    const suffix = Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    const code   = `${prefix}-${suffix}`;
    const { data } = await supabase
      .from('companies')
      .select('id')
      .eq('company_code', code)
      .maybeSingle();
    if (!data) return code; // unique — use it
  }
  // Fallback with timestamp entropy
  return `${prefix}-${Date.now().toString(36).slice(-4).toUpperCase()}`;
}

export async function getOrCreateCompany(userId, companyName) {
  const { data: existing } = await supabase
    .from('companies')
    .select('*')
    .eq('owner_id', userId)
    .single();
  if (existing) return existing;

  const code = await generateCompanyCode(companyName || 'My Company');
  const { data, error } = await supabase
    .from('companies')
    .insert({ owner_id: userId, name: companyName || 'My Company', company_code: code })
    .select()
    .single();
  if (error) console.error('getOrCreateCompany error:', error);
  return data;
}

export async function getCompanyByCode(code) {
  if (!code) return null;
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .ilike('company_code', code.trim().toUpperCase())
    .maybeSingle();
  if (error && error.code !== 'PGRST116') console.error('getCompanyByCode error:', error);
  return data || null;
}

export async function joinCompanyByCode(userId, code) {
  const company = await getCompanyByCode(code);
  if (!company) return { success: false, error: 'Invalid company code. Please check and try again.' };
  if (company.owner_id === userId) return { success: false, error: 'You are already the owner of this company.' };

  // Check not already a member
  const { data: existing } = await supabase
    .from('company_members')
    .select('id')
    .eq('company_id', company.id)
    .eq('user_id', userId)
    .maybeSingle();
  if (existing) return { success: true, company }; // already a member — silently succeed

  const { data, error } = await supabase
    .from('company_members')
    .insert({
      company_id:      company.id,
      user_id:         userId,
      invited_email:   '', // filled in by trigger/post-process if needed
      invite_accepted: true,
      role:            'member',
    })
    .select()
    .single();
  if (error) { console.error('joinCompanyByCode error:', error); return { success: false, error: 'Could not join company. Please try again.' }; }
  return { success: true, company, member: data };
}

export async function getCompany(userId) {
  // Check if owner
  const { data: owned } = await supabase
    .from('companies')
    .select('*')
    .eq('owner_id', userId)
    .single();
  if (owned) return owned;

  // Check if accepted member
  const { data: membership } = await supabase
    .from('company_members')
    .select('company_id')
    .eq('user_id', userId)
    .eq('invite_accepted', true)
    .single();
  if (!membership) return null;

  const { data: company } = await supabase
    .from('companies')
    .select('*')
    .eq('id', membership.company_id)
    .single();
  return company || null;
}

export async function getCompanyMembers(companyId) {
  const { data, error } = await supabase
    .from('company_members')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: true });
  if (error) console.error('getCompanyMembers error:', error);
  if (!data || data.length === 0) return [];

  const userIds = data.filter(m => m.user_id).map(m => m.user_id);
  let profiles = {};
  if (userIds.length > 0) {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('id, email, company_name')
      .in('id', userIds);
    (profileData || []).forEach(p => { profiles[p.id] = p; });
  }

  return data.map(m => ({
    ...m,
    profile: m.user_id ? (profiles[m.user_id] || null) : null,
  }));
}

export async function inviteMember(companyId, email, role, assignedFrameworks) {
  const { data, error } = await supabase
    .from('company_members')
    .insert({
      company_id: companyId,
      invited_email: email.toLowerCase().trim(),
      role: role || 'member',
      invite_accepted: false,
      assigned_frameworks: assignedFrameworks || [],
    })
    .select()
    .single();
  if (error) console.error('inviteMember error:', error);
  return data;
}

export async function acceptCompanyInvite(token, userId) {
  const { data, error } = await supabase
    .from('company_members')
    .update({ invite_accepted: true, user_id: userId })
    .eq('invite_token', token)
    .select('*, companies(*)')
    .single();
  if (error) console.error('acceptCompanyInvite error:', error);
  return data;
}

export async function updateMemberFrameworks(memberId, frameworks) {
  const { error } = await supabase
    .from('company_members')
    .update({ assigned_frameworks: frameworks || [] })
    .eq('id', memberId);
  if (error) console.error('updateMemberFrameworks error:', error);
  return !error;
}

export async function removeMember(memberId) {
  const { error } = await supabase
    .from('company_members')
    .delete()
    .eq('id', memberId);
  if (error) console.error('removeMember error:', error);
  return !error;
}

export async function updateMemberRole(memberId, role) {
  const { error } = await supabase
    .from('company_members')
    .update({ role })
    .eq('id', memberId);
  if (error) console.error('updateMemberRole error:', error);
  return !error;
}

export async function checkPendingInvite(email) {
  if (!email) return null;
  const { data, error } = await supabase
    .from('company_members')
    .select('*, companies(*)')
    .eq('invited_email', email.toLowerCase().trim())
    .eq('invite_accepted', false)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') console.error('checkPendingInvite error:', error);
  return data || null;
}

// ── COMPANY INVITES (new invite flow) ─────────────────────────

export async function createCompanyInvite(companyId, email, role, invitedBy, assignedFrameworks) {
  const { data, error } = await supabase
    .from('company_invites')
    .upsert(
      {
        company_id:          companyId,
        email:               email.toLowerCase().trim(),
        role:                role || 'member',
        invited_by:          invitedBy || null,
        assigned_frameworks: assignedFrameworks || [],
        // reset expiry on re-invite
        expires_at:          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        accepted_at:         null,
      },
      { onConflict: 'company_id,email' }
    )
    .select()
    .single();
  if (error) console.error('createCompanyInvite error:', error);
  return data;
}

export async function getCompanyInvites(companyId) {
  const { data, error } = await supabase
    .from('company_invites')
    .select('*')
    .eq('company_id', companyId)
    .is('accepted_at', null)
    .order('created_at', { ascending: false });
  if (error) console.error('getCompanyInvites error:', error);
  return data || [];
}

export async function lookupCompanyInviteByToken(token) {
  if (!token) return null;
  const { data, error } = await supabase
    .from('company_invites')
    .select('*, companies(id, name, company_code)')
    .eq('token', token)
    .is('accepted_at', null)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') console.error('lookupCompanyInviteByToken error:', error);
  return data || null;
}

export async function acceptCompanyInviteByToken(token, userId) {
  // Fetch the invite
  const invite = await lookupCompanyInviteByToken(token);
  if (!invite) return { success: false, error: 'Invite not found or already used.' };
  if (new Date(invite.expires_at) < new Date()) return { success: false, error: 'This invite has expired.' };

  // Mark accepted
  const { error: updateErr } = await supabase
    .from('company_invites')
    .update({ accepted_at: new Date().toISOString() })
    .eq('token', token);
  if (updateErr) { console.error('acceptCompanyInviteByToken update error:', updateErr); return { success: false, error: 'Failed to accept invite.' }; }

  // Create member record (ignore duplicate)
  const { error: memberErr } = await supabase
    .from('company_members')
    .upsert(
      {
        company_id:          invite.company_id,
        user_id:             userId,
        invited_email:       invite.email,
        role:                invite.role,
        invite_accepted:     true,
        assigned_frameworks: invite.assigned_frameworks || [],
        invited_by:          invite.invited_by || null,
        accepted_at:         new Date().toISOString(),
        status:              'active',
      },
      { onConflict: 'company_id,user_id' }
    );
  if (memberErr) console.error('acceptCompanyInviteByToken member error:', memberErr);

  return { success: true, companyName: invite.companies?.name || 'your team', companyId: invite.company_id };
}

export async function revokeCompanyInvite(inviteId) {
  const { error } = await supabase
    .from('company_invites')
    .delete()
    .eq('id', inviteId);
  if (error) console.error('revokeCompanyInvite error:', error);
  return !error;
}

// ── USAGE CAPS ────────────────────────────────────────────────

export async function getMonthlyDocumentReads(userId) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const { count, error } = await supabase
    .from('document_reads')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', monthStart);
  if (error) console.error('getMonthlyDocumentReads error:', error);
  return count || 0;
}

export async function recordDocumentRead(userId) {
  const { error } = await supabase
    .from('document_reads')
    .insert({ user_id: userId });
  if (error) console.error('recordDocumentRead error:', error);
  return !error;
}

export async function getMonthlyAIMessages(userId) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const { count, error } = await supabase
    .from('ai_messages')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', monthStart);
  if (error) console.error('getMonthlyAIMessages error:', error);
  return count || 0;
}

export async function recordAIMessage(userId) {
  const { error } = await supabase
    .from('ai_messages')
    .insert({ user_id: userId });
  if (error) console.error('recordAIMessage error:', error);
  return !error;
}

export async function getAdminStats() {
  const { data: users, error } = await supabase
    .from('profiles')
    .select('plan, created_at, last_active, account_status');
  if (error) { console.error('getAdminStats error:', error); return null; }

  const allUsers   = users || [];
  const now        = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const usersByPlan = { professional: 0, business: 0, starter: 0, pro: 0, team: 0, agency: 0 };
  let newThisMonth    = 0;
  let activeUsers     = 0;

  allUsers.forEach(u => {
    const plan = (u.plan || 'professional').toLowerCase();
    usersByPlan[plan] = (usersByPlan[plan] || 0) + 1;
    if (u.created_at && new Date(u.created_at) >= monthStart) newThisMonth++;
    if (u.last_active && new Date(u.last_active) >= monthStart) activeUsers++;
  });

  const PLAN_PRICES = { professional: 399, business: 799, starter: 0, pro: 399, team: 799, agency: 799 };
  const estimatedMRR = Object.entries(usersByPlan).reduce(
    (sum, [plan, count]) => sum + (PLAN_PRICES[plan] || 0) * count, 0
  );

  return { totalUsers: allUsers.length, usersByPlan, newThisMonth, activeUsers, estimatedMRR };
}

// ── CONTROL HISTORY (Audit Trail) ─────────────────────────────

export async function logControlChange(userId, companyId, controlId, framework, oldStatus, newStatus, changedByName = '', notes = '') {
  if (!userId || !controlId) return;
  const { error } = await supabase.from('control_history').insert({
    user_id:          userId,
    company_id:       companyId || null,
    control_id:       controlId,
    framework:        framework || 'cmmc_l2',
    old_status:       oldStatus || null,
    new_status:       newStatus,
    changed_by:       userId,
    changed_by_name:  changedByName,
    notes,
  });
  if (error) console.error('logControlChange error:', error);
}

export async function getControlHistory(userId, controlId, limit = 10) {
  const { data, error } = await supabase
    .from('control_history')
    .select('*')
    .eq('user_id', userId)
    .eq('control_id', controlId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) console.error('getControlHistory error:', error);
  return data || [];
}

export async function getFullControlHistory(userId) {
  const { data, error } = await supabase
    .from('control_history')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) console.error('getFullControlHistory error:', error);
  return data || [];
}

// ── CONTROL NARRATIVES ────────────────────────────────────────

export async function getNarratives(userId, framework = 'cmmc_l2') {
  const { data, error } = await supabase
    .from('control_narratives')
    .select('*')
    .eq('user_id', userId)
    .eq('framework', framework);
  if (error) console.error('getNarratives error:', error);
  return data || [];
}

export async function saveNarrative(userId, controlId, framework, narrative) {
  const { error } = await supabase
    .from('control_narratives')
    .upsert(
      {
        user_id:              userId,
        control_id:           controlId,
        framework:            framework || 'cmmc_l2',
        narrative,
        narrative_updated_at: new Date().toISOString(),
        narrative_updated_by: userId,
      },
      { onConflict: 'user_id,control_id,framework' }
    );
  if (error) console.error('saveNarrative error:', error);
  return !error;
}

// ── SHARE LINKS ───────────────────────────────────────────────

export async function createShareLink(userId, opts = {}) {
  const { data, error } = await supabase
    .from('share_links')
    .insert({
      user_id:       userId,
      label:         opts.label         || 'Compliance Report',
      framework:     opts.framework     || 'cmmc_l2',
      show_controls: opts.showControls  !== false,
      show_evidence: opts.showEvidence  !== false,
      show_tasks:    opts.showTasks     || false,
      show_score:    opts.showScore     !== false,
      expires_at:    opts.expiresAt     || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    })
    .select()
    .single();
  if (error) console.error('createShareLink error:', error);
  return data;
}

export async function getShareLinks(userId) {
  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) console.error('getShareLinks error:', error);
  return data || [];
}

export async function updateShareLink(linkId, updates) {
  const { error } = await supabase
    .from('share_links')
    .update(updates)
    .eq('id', linkId);
  if (error) console.error('updateShareLink error:', error);
  return !error;
}

export async function deleteShareLink(linkId) {
  const { error } = await supabase.from('share_links').delete().eq('id', linkId);
  if (error) console.error('deleteShareLink error:', error);
  return !error;
}

export async function getShareLinkByToken(token) {
  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('token', token)
    .eq('is_active', true)
    .gt('expires_at', new Date().toISOString())
    .maybeSingle();
  if (error) console.error('getShareLinkByToken error:', error);
  return data || null;
}

export async function incrementShareLinkViews(linkId) {
  const { error } = await supabase.rpc('increment_share_link_views', { link_id: linkId });
  // fallback: just fetch + update
  if (error) {
    const { data: link } = await supabase.from('share_links').select('view_count').eq('id', linkId).single();
    if (link) {
      await supabase.from('share_links').update({ view_count: (link.view_count || 0) + 1 }).eq('id', linkId);
    }
  }
}